/**
 * Navigation tracking functions.
 * Handles Navigation Clicked and Header Link Clicked events.
 *
 * @module tracking/navigation
 *
 * These internal functions are used by the Tracker factory (tracker.ts).
 * For component usage, use the tracker instance via `getHeaderTracker()` or `getFooterTracker()`.
 *
 * @example
 * // In components, use the tracker factory instead of these internal functions:
 * import { getHeaderTracker } from '../tracking'
 *
 * getHeaderTracker().trackLink({
 *   navigationDetailPath: ['business-cards', 'premium'],
 *   href: '/products/business-cards',
 * })
 */

import type { LinkTypes, NavigationSectionType } from '../types/tracking'
import { TRACKING_EVENTS, TRACKING_CATEGORIES } from '../constants/analytics'
import { getTrackingConfig, safeTrack } from './config'

/**
 * Internal function that handles the actual navigation tracking call.
 * Used by the Tracker factory - do not call directly from components.
 *
 * @param bookendName - The name of the bookend component (e.g., 'header', 'footer')
 * @param navigationDetail - The pipe-separated navigation path
 * @param destinationPath - The destination URL path
 * @param subSection - Optional subsection identifier (e.g., 'TopNav')
 * @param linkType - Optional link type for analytics
 * @param navigationSection - Optional navigation section type
 *
 * @internal
 */
export const trackNavigationInternal = (
  bookendName: string,
  navigationDetail: string,
  destinationPath: string,
  subSection?: string,
  linkType?: LinkTypes,
  navigationSection?: NavigationSectionType,
): void => {
  const trackingConfig = getTrackingConfig()
  const currentPath = globalThis.window?.location?.pathname ?? ''
  const eventLabel = `${bookendName}${subSection ? ':' + subSection : ''}`

  safeTrack(TRACKING_EVENTS.NAVIGATION_CLICKED, {
    label: eventLabel,
    eventDetail: `${currentPath};${destinationPath};${subSection || bookendName};${navigationDetail};${linkType || ''}`,
    navigationDetail,
    navigationSection,
    ...trackingConfig,
    category: trackingConfig.pageSection,
  })
}

/**
 * Internal function that handles the actual header link tracking call.
 * Used by the Tracker factory - do not call directly from components.
 *
 * @param bookendName - The name of the bookend component (e.g., 'header', 'footer')
 * @param navigationDetail - The pipe-separated navigation path
 * @param subSection - Optional subsection identifier (e.g., 'HamburgerMenu')
 * @param navigationSection - Optional navigation section type
 *
 * @internal
 */
export const trackHeaderLinkInternal = (
  bookendName: string,
  navigationDetail: string,
  subSection?: string,
  navigationSection?: NavigationSectionType,
): void => {
  const trackingConfig = getTrackingConfig()
  const eventLabel = `${bookendName}${subSection ? ':' + subSection : ''}`

  safeTrack(TRACKING_EVENTS.HEADER_LINK_CLICKED, {
    ...trackingConfig,
    category: TRACKING_CATEGORIES.HEADER,
    label: eventLabel,
    eventDetail: navigationDetail,
    navigationSection,
  })
}
